<script setup lang="ts">
import type { Article } from 'schema-dts'

const route = useRoute()

const { data, status, error } = await useFetch('/internals/post/find', {
  method: 'GET',
  lazy: true,
  query: {
    path: route.path,
  },
})

const { isLoading } = useIsLoading(status)

if (error.value) {
  throw createError({ ...error.value, fatal: true })
}

const metaTitle = computed(() => useHeadTitle(data.value?.seo_title || data.value?.title))
const metaImage = useSeoImageURL(data.value?.featured_image.url, { width: META_IMAGE_WIDTH, height: META_IMAGE_HEIGHT })
const requestURL = useRequestURL()

useSeoMeta({
  title: metaTitle,
  ogTitle: metaTitle,
  twitterTitle: metaTitle,
  description: data.value?.description,
  ogDescription: data.value?.description,
  twitterDescription: data.value?.description,
  ogImage: metaImage,
  ogImageWidth: META_IMAGE_WIDTH,
  ogImageHeight: META_IMAGE_HEIGHT,
  twitterImage: metaImage,
  ogUrl: requestURL.href,
})

const { topLevelLocale } = useTopLevelLocale()

const authorDisplayedName = computed(() => `${data.value?.author.first_name} ${data.value?.author.last_name}`)

useHead({
  link: [
    { rel: 'canonical', href: requestURL.href },
  ],
  script: [
    getLdJsonScript<Article>({
      '@type': 'BlogPosting',
      'name': data.value?.title,
      'description': data.value?.description,
      'url': requestURL.href,
      'datePublished': data.value?.first_published_at,
      'dateModified': data.value?.last_modified_at,
      'inLanguage': topLevelLocale.value,
      'author': {
        '@type': 'Person',
        'name': authorDisplayedName.value,
      },
    }),
  ],
})
</script>

<template>
  <div>
    <LazyAppPageLoading v-if="isLoading" />
    <template v-else-if="data">
      <AppBreadcrumb
        :crumbs="data.breadcrumbs"
        :current-title="data.title"
      />

      <AppHeaderPage
        :tags="data.tags"
        :image="data.featured_image"
        :image-caption="data.featured_image"
        :variant="data.header_template"
      >
        <template #title>
          {{ data.title }}
        </template>

        <template
          v-if="data.sub_title"
          #subtitle
        >
          {{ data.sub_title }}
        </template>
      </AppHeaderPage>

      <div
        v-if="data.include_toc"
        class="hidden lg:block lg:mb-12"
      >
        <LazyAppBlogToc
          selector="#post-content"
        />
      </div>

      <section
        id="post-content"
        class="space-y-6 md:space-y-12"
      >
        <AppPostBlock
          v-for="block in data.body"
          :id="block.id"
          :key="block.id"
          :type="block.type"
          :content="block.value"
        />

        <LazyAppPostAuthor
          v-if="data.author"
          :avatar="data.author.avatar"
          :name="`${data.author.first_name} ${data.author.last_name}`"
        >
          <template #role>
            {{ data.author.role }}
          </template>
          <template
            v-if="data.author.bio"
            #bio
          >
            <!-- eslint-disable vue/no-v-html -->
            <div v-html="data.author.bio" />
            <!-- eslint-enable vue/no-v-html -->
          </template>
        </LazyAppPostAuthor>
      </section>

      <LazyAppRelatedPostList
        v-if="data.related_post_ids?.length > 2"
        :page-data-cache-key="route.path"
        :related-posts="data.related_post_ids"
      />
    </template>
  </div>
</template>
